import { SvelteMap } from "svelte/reactivity";
import type { PresenceStatus } from "#lib/api-client/types.gen.js";

interface PresenceEntry {
	id: string;
	status: PresenceStatus;
}

/**
 * Live presence for the users page and the case-file share picker.
 *
 * Status is one of online (active), away (idle past the threshold) or offline
 * (no live session). The users list already carries `presence_status` at fetch
 * time; this store keeps an open page current from WebSocket
 * `presence.changed` frames and re-syncs after a reconnect.
 */
class PresenceStore {
	#statuses = new SvelteMap<string, PresenceStatus>();

	/** The current status for a user. */
	status(userID: string | null | undefined): PresenceStatus {
		if (!userID) return "offline";
		return this.#statuses.get(userID) ?? "offline";
	}

	/** Whether the user has a live session (online or away). */
	isOnline(userID: string | null | undefined): boolean {
		return this.status(userID) !== "offline";
	}

	/** Applies one presence frame. */
	setStatus(userID: string, status: PresenceStatus | string): void {
		if (!userID) return;
		const normalized: PresenceStatus =
			status === "online" || status === "away" || status === "offline" ? status : "offline";
		if (normalized === "offline") {
			this.#statuses.delete(userID);
		} else {
			this.#statuses.set(userID, normalized);
		}
	}

	/** Replaces the whole snapshot (initial load, reconnect re-sync). */
	replace(entries: PresenceEntry[]): void {
		this.#statuses.clear();
		for (const entry of entries) {
			if (entry?.id && entry.status !== "offline") {
				this.#statuses.set(entry.id, entry.status);
			}
		}
	}

	/** Adds entries without clearing existing knowledge (pagination). */
	merge(entries: PresenceEntry[]): void {
		for (const entry of entries) {
			if (entry?.id && entry.status !== "offline") {
				this.#statuses.set(entry.id, entry.status);
			}
		}
	}

	/** Drops all presence state (logout). */
	clear(): void {
		this.#statuses.clear();
	}
}

export const presence = new PresenceStore();
